import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { useAuthContext } from "../../../context-state/AuthContext";
import { useDashboardContext } from "../../../context-state/DashboardContext";
import { EventService } from "../../../services/eventService";
import { useSnackbar } from "../../../components/minimal/snackbar";
import { useLocales } from "../../../locales";
import { LinearLoader } from "../../../components/linear-loader/LinearLoader";

export const EventGuard = () => {
    const { account } = useAuthContext();
    const { enqueueSnackbar } = useSnackbar();
    const { translate } = useLocales();
    const [isLoading, setIsLoading] = useState(false);
    const {
        eventContext,
        userEventContext,
        eventOrderContext,
        ticketContext,
        couponContext,
        utmContext,
        linkContext,
    } = useDashboardContext();


    const { getEvent } = eventContext;
    const {
        updateManyEventOrders,
        updateEventToEventOrdersRelation,
        getEventOrdersOfEvent,
    } = eventOrderContext;
    const {
        updateManyUserEvents,
        updateEventToUserEventRelation,
    } = userEventContext;
    const {
        updateManyCoupons,
        updateEventToCouponsRelation,
    } = couponContext;
    const {
        getTicketsOfEvent,
        updateManyTickets,
        updateEventToTicketsRelation,
    } = ticketContext;
    const {
        updateManyUtms,
        updateEventToUtmsRelation,
    } = utmContext;
    const {
        updateManyLinks,
        updateEventToLinksRelation,
    } = linkContext;

    const { eventId } = useParams();
    const location = useLocation();

    const event = getEvent(eventId);
    const eventTickets = getTicketsOfEvent(eventId);
    const eventOrders = getEventOrdersOfEvent(eventId);

    const lastFetchedPath = useRef(null);

    const getAllUserEventRelevantData = async () => {
        try {
            setIsLoading(true)
            const {
                userEvents,
                eventOrders,
                tickets,
                coupons,
                utms,
                links,
            } = await EventService.getEventDashboardData({
                accountId: account._id,
                eventId: event._id,
            });

            // Tickets
            const normalizedTickets = tickets || []
            updateManyTickets(normalizedTickets);
            updateEventToTicketsRelation(event._id, normalizedTickets);

            // UserEvent
            const normalizedUserEvents = userEvents || []
            updateManyUserEvents(normalizedUserEvents)
            updateEventToUserEventRelation(event._id, normalizedUserEvents)

            // EventOrders
            const normalizedEventOrders = eventOrders || []
            updateManyEventOrders(normalizedEventOrders)
            updateEventToEventOrdersRelation(event._id, normalizedEventOrders)

            // Coupons
            const normalizedCoupons = coupons || []
            updateManyCoupons(normalizedCoupons)
            updateEventToCouponsRelation(event._id, normalizedCoupons)

            // Utms
            const normalizedUtms = utms || []
            updateManyUtms(normalizedUtms)
            updateEventToUtmsRelation(event._id, normalizedUtms)

            // Links
            const normalizedLinks = links || []
            updateManyLinks(normalizedLinks)
            updateEventToLinksRelation(event._id, normalizedLinks)

        } catch (e) {
            console.error(e);
            enqueueSnackbar(translate("general_error"), { variant: "error" });
        }finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!event) return;

        const pathChanged = location.pathname !== lastFetchedPath.current;
        const dataMissing = !eventTickets || !eventOrders;

        // ✅ Only refetch for specific tabs (orders, coupons, seats-chart)
        const shouldRefetchForTab = /\/(orders|coupons|seats-chart)(\/)?$/.test(location.pathname);

        if (dataMissing || (pathChanged && shouldRefetchForTab)) {
            lastFetchedPath.current = location.pathname;
            getAllUserEventRelevantData();
        }
    }, [location.pathname, event?._id, eventTickets?.length, eventOrders?.length]);

    const isAllRelevantEventDataFetched = event && eventTickets && eventOrders;

    if(isLoading || !isAllRelevantEventDataFetched) {
        return  <LinearLoader />
    }

    return <Outlet />;
};

